/**
 * Data Protection / Privacy Settings – dashboard-wt (green/black theme)
 */

import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useI18n, useToast } from '../providers';
import { UserDashboardLayout } from '../components/user';
import { getSettings, updateSettings } from '../services/users';
import DataExport from '../components/user/DataExport';
import AccountDeletion from '../components/user/AccountDeletion';
import Switch from '../components/common/Switch';
import Select from '../components/common/Select';
import Spinner from '../components/common/Spinner';
import '../styles/landing.css';
import '../styles/global.css';

const PrivacySettings = () => {
  const navigate = useNavigate();
  const { language } = useI18n();
  const { showSuccess, showError } = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('privacy');
  const [contentInView, setContentInView] = useState(true);
  const contentRef = useRef(null);
  const [settings, setSettings] = useState({
    notifications: { email: true, sms: true, push: true },
    privacy: { showProfile: true, showRatings: true, showStats: true },
    language: 'en',
  });

  useEffect(() => {
    loadSettings();
  }, []);

  useEffect(() => {
    const el = contentRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      (entries) => { entries.forEach((e) => e.isIntersecting && setContentInView(true)); },
      { threshold: 0.1 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [activeTab]);

  const loadSettings = async () => {
    try {
      setLoading(true);
      const response = await getSettings();
      if (response?.data) setSettings(response.data);
    } catch (err) {
      showError(err?.message || (language === 'fr' ? 'Échec du chargement des paramètres' : 'Failed to load settings'));
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await updateSettings(settings);
      showSuccess(language === 'fr' ? 'Paramètres enregistrés.' : 'Settings saved.');
    } catch (err) {
      showError(err?.message || (language === 'fr' ? 'Échec de l\'enregistrement' : 'Failed to save'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <UserDashboardLayout>
        <div className="flex justify-center items-center min-h-[60vh]">
          <Spinner size="lg" />
        </div>
      </UserDashboardLayout>
    );
  }

  const t = {
    back: language === 'fr' ? 'Retour aux paramètres' : 'Back to Settings',
    title: language === 'fr' ? 'Vos Données, Protégées.' : 'Your Data, Protected.',
    sub: language === 'fr'
      ? 'Gérez la confidentialité et vos droits (RGPD).'
      : 'Manage privacy and your data rights (GDPR).',
    tabPrivacy: language === 'fr' ? 'Confidentialité' : 'Privacy',
    tabData: language === 'fr' ? 'Données' : 'Data',
    privacyTitle: language === 'fr' ? 'Paramètres de confidentialité' : 'Privacy Settings',
    privacyDesc: language === 'fr' ? 'Contrôlez qui voit vos informations' : 'Control who sees your information',
    showProfile: language === 'fr' ? 'Afficher mon profil' : 'Show my profile',
    showRatings: language === 'fr' ? 'Afficher mes évaluations' : 'Show my ratings',
    showStats: language === 'fr' ? 'Afficher mes statistiques' : 'Show my transaction stats',
    notifTitle: language === 'fr' ? 'Notifications' : 'Notification Preferences',
    notifDesc: language === 'fr' ? 'Comment recevoir les alertes' : 'How you receive alerts',
    notifEmail: language === 'fr' ? 'Email' : 'Email',
    notifSms: language === 'fr' ? 'SMS' : 'SMS',
    notifPush: language === 'fr' ? 'Push' : 'Push',
    langTitle: language === 'fr' ? 'Langue' : 'Language',
    langDesc: language === 'fr' ? 'Langue préférée' : 'Preferred language',
    save: language === 'fr' ? 'Enregistrer' : 'Save',
    saving: language === 'fr' ? 'Enregistrement…' : 'Saving…',
    gdprTitle: language === 'fr' ? 'Vos droits (RGPD)' : 'Your Data Rights (GDPR)',
    gdprDesc: language === 'fr' ? 'Exportez ou supprimez vos données' : 'Export or delete your data',
  };

  return (
    <UserDashboardLayout>
      <div className="dashboard-wt">
        {/* Hero – compact, no orbs */}
        <section className="dashboard-wt-hero min-h-[280px] flex flex-col justify-center">
          <div className="dashboard-wt-hero-bg" aria-hidden="true" />
          <div className="dashboard-wt-hero-grid" aria-hidden="true" />
          <div className="dashboard-wt-hero-noise" aria-hidden="true" />
          <div className="dashboard-wt-hero-inner relative z-10 text-center max-w-2xl mx-auto w-full px-4">
            <button
              type="button"
              onClick={() => navigate('/settings')}
              className="dashboard-wt-btn-secondary w-auto mb-6 !inline-flex"
            >
              ← {t.back}
            </button>
            <h1 className="dashboard-wt-hero-title text-3xl md:text-4xl mb-2">
              {language === 'fr' ? 'Vos Données,' : 'Your Data,'} <span>{language === 'fr' ? 'Protégées.' : 'Protected.'}</span>
            </h1>
            <p className="dashboard-wt-hero-sub text-base md:text-lg">{t.sub}</p>
          </div>
        </section>

        {/* Tabs */}
        <section className="dashboard-wt-section">
          <div className="dashboard-wt-section-inner">
            <div className="flex gap-2 p-1 rounded-xl bg-[rgba(255,255,255,0.05)] border border-[var(--wt-border)] mb-8 max-w-md mx-auto">
              {[
                { id: 'privacy', label: t.tabPrivacy, icon: '🔒' },
                { id: 'data', label: t.tabData, icon: '📊' },
              ].map((tab) => (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex-1 flex items-center justify-center gap-2 py-2.5 px-4 rounded-lg text-sm font-semibold transition-all ${
                    activeTab === tab.id
                      ? 'bg-[var(--wt-green)] text-[var(--wt-black)] shadow-[0_0_20px_var(--wt-green-glow)]'
                      : 'text-[var(--wt-muted)] hover:text-white hover:bg-white/5'
                  }`}
                >
                  <span>{tab.icon}</span>
                  <span>{tab.label}</span>
                </button>
              ))}
            </div>

            {/* Privacy tab */}
            {activeTab === 'privacy' && (
              <div className={`dashboard-wt-grid ${contentInView ? 'is-inview' : ''}`} ref={contentRef}>
                <div className="dashboard-wt-card">
                  <div className="dashboard-wt-card-header flex items-start gap-4">
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center text-2xl flex-shrink-0">🔒</div>
                    <div>
                      <h2 className="dashboard-wt-card-title">{t.privacyTitle}</h2>
                      <p className="dashboard-wt-card-desc !mb-0">{t.privacyDesc}</p>
                    </div>
                  </div>
                  <div className="dashboard-wt-card-content space-y-4">
                    {[
                      { key: 'showProfile', icon: '👤', label: t.showProfile },
                      { key: 'showRatings', icon: '⭐', label: t.showRatings },
                      { key: 'showStats', icon: '📊', label: t.showStats },
                    ].map((item) => (
                      <div key={item.key} className="flex items-center justify-between py-2 border-b border-[var(--wt-border)] last:border-0">
                        <span className="flex items-center gap-2 text-white">
                          <span>{item.icon}</span>
                          <span>{item.label}</span>
                        </span>
                        <Switch
                          checked={settings.privacy?.[item.key] ?? true}
                          onChange={(checked) =>
                            setSettings({
                              ...settings,
                              privacy: { ...settings.privacy, [item.key]: checked },
                            })
                          }
                          variant={settings.privacy?.[item.key] ? 'success' : 'default'}
                        />
                      </div>
                    ))}
                  </div>
                </div>

                <div className="dashboard-wt-card">
                  <div className="dashboard-wt-card-header flex items-start gap-4">
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center text-2xl flex-shrink-0">🔔</div>
                    <div>
                      <h2 className="dashboard-wt-card-title">{t.notifTitle}</h2>
                      <p className="dashboard-wt-card-desc !mb-0">{t.notifDesc}</p>
                    </div>
                  </div>
                  <div className="dashboard-wt-card-content space-y-4">
                    {[
                      { type: 'email', icon: '📧', label: t.notifEmail },
                      { type: 'sms', icon: '💬', label: t.notifSms },
                      { type: 'push', icon: '🔔', label: t.notifPush },
                    ].map((item) => (
                      <div key={item.type} className="flex items-center justify-between py-2 border-b border-[var(--wt-border)] last:border-0">
                        <span className="flex items-center gap-2 text-white">
                          <span>{item.icon}</span>
                          <span>{item.label}</span>
                        </span>
                        <Switch
                          checked={settings.notifications?.[item.type] ?? true}
                          onChange={(checked) =>
                            setSettings({
                              ...settings,
                              notifications: { ...settings.notifications, [item.type]: checked },
                            })
                          }
                          variant={settings.notifications?.[item.type] ? 'success' : 'default'}
                        />
                      </div>
                    ))}
                  </div>
                </div>

                <div className="dashboard-wt-card">
                  <div className="dashboard-wt-card-header flex items-start gap-4">
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-green-500 to-emerald-500 flex items-center justify-center text-2xl flex-shrink-0">🌍</div>
                    <div>
                      <h2 className="dashboard-wt-card-title">{t.langTitle}</h2>
                      <p className="dashboard-wt-card-desc !mb-0">{t.langDesc}</p>
                    </div>
                  </div>
                  <div className="dashboard-wt-card-content">
                    <label className="block text-sm font-medium text-[var(--wt-muted)] mb-2">{t.langTitle}</label>
                    <Select
                      value={settings.language || 'en'}
                      onChange={(e) => setSettings({ ...settings, language: e.target.value })}
                      options={[
                        { value: 'en', label: '🇬🇧 English' },
                        { value: 'fr', label: '🇫🇷 Français' },
                      ]}
                      className="bg-[#000] border-[var(--wt-border)] text-white rounded-lg"
                    />
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'privacy' && (
              <div className="flex justify-end mt-8">
                <button
                  type="button"
                  onClick={handleSave}
                  disabled={saving}
                  className="dashboard-wt-btn-primary w-auto min-w-[160px]"
                >
                  {saving ? (
                    <>
                      <Spinner size="sm" className="mr-2" />
                      {t.saving}
                    </>
                  ) : (
                    <>💾 {t.save}</>
                  )}
                </button>
              </div>
            )}

            {/* Data tab */}
            {activeTab === 'data' && (
              <div className={`dashboard-wt-grid ${contentInView ? 'is-inview' : ''}`} ref={contentRef}>
                <div className="dashboard-wt-card col-span-full">
                  <div className="dashboard-wt-card-header flex items-start gap-4">
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center text-2xl flex-shrink-0">📊</div>
                    <div>
                      <h2 className="dashboard-wt-card-title">{t.gdprTitle}</h2>
                      <p className="dashboard-wt-card-desc !mb-0">{t.gdprDesc}</p>
                    </div>
                  </div>
                </div>
                <div className="dashboard-wt-card">
                  <DataExport />
                </div>
                <div className="dashboard-wt-card">
                  <AccountDeletion />
                </div>
              </div>
            )}
          </div>
        </section>
      </div>
    </UserDashboardLayout>
  );
};

export default PrivacySettings;
